import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getAdminToken } from '../services/authService';
import { useTheme } from '../ThemeContext';
import {
  Box,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableBody as MuiTableBody,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  Paper,
  IconButton,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Button as MuiButton,
  Chip,
  Tabs,
  Tab,
  Card as MuiCard,
  CardContent,
  Badge as MuiBadge,
  Alert as MuiAlert,
} from '@mui/material';
import { FaEye, FaFilter, FaCalendarAlt, FaList, FaChartLine } from 'react-icons/fa';
import { TbFilter } from "react-icons/tb";
import axios from 'axios';
import {
  Button,
  Card,
  Form,
  Alert,
  Spinner,
  Badge,
  FloatingLabel,
  Row,
  Col,
  Table,
  InputGroup
} from 'react-bootstrap';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const UnifiedLogs = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  // System Logs states (from Logs.jsx)
  const [chartData, setChartData] = useState(null);
  const [systemLogs, setSystemLogs] = useState([]);
  const [filteredSystemLogs, setFilteredSystemLogs] = useState([]);
  const [systemLogsError, setSystemLogsError] = useState('');
  const [viewing, setViewing] = useState({});
  const [systemLogsLoading, setSystemLogsLoading] = useState(false);
  
  // Activity Logs states (from ActivityLog.jsx)
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsError, setActivityLogsError] = useState('');
  
  const { isDarkMode } = useTheme();
  const datePickerRef = useRef(null);
  
  // System Logs filters
  const [systemFilters, setSystemFilters] = useState({
    timeRange: 'day',
    startDate: null,
    endDate: null,
    customRange: false
  });

  // Activity Logs filters
  const [activityFilters, setActivityFilters] = useState({
    activity_type: "",
    username: "",
    status: ""
  });

  // System Logs chart data generation
  const generateChartData = useCallback((matrixData) => {
    if (!matrixData || matrixData.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'Logs per Day',
          data: [],
          borderColor: isDarkMode ? '#90caf9' : 'rgba(153, 102, 255, 1)',
          backgroundColor: isDarkMode ? 'rgba(144, 202, 249, 0.4)' : 'rgba(153, 102, 255, 0.6)',
          fill: false,
          tension: 0.3,
        }]
      };
    }

    const labels = matrixData.map(row => row.date).sort();
    const counts = matrixData.map(row => row.count);
    
    return {
      labels,
      datasets: [{
        label: 'Logs per Day',
        data: counts,
        borderColor: isDarkMode ? '#90caf9' : 'rgba(153, 102, 255, 1)',
        backgroundColor: isDarkMode ? 'rgba(144, 202, 249, 0.4)' : 'rgba(153, 102, 255, 0.6)',
        fill: false,
        tension: 0.3,
      }]
    };
  }, [isDarkMode]);

  // Fetch System Logs
  const fetchSystemLogs = useCallback(async () => {
    setSystemLogsLoading(true);
    try {
      const token = getAdminToken();
      
      const queryParams = new URLSearchParams({
        filter: systemFilters.timeRange,
        format: 'matrix'
      });
      
      if (systemFilters.timeRange === 'custom') {
        if (!systemFilters.startDate || !systemFilters.endDate) {
          setSystemLogsLoading(false);
          setSystemLogsError('');
          return;
        }
        
        if (systemFilters.startDate > systemFilters.endDate) {
          setSystemLogsError('Start date cannot be after end date');
          setSystemLogsLoading(false);
          return;
        }
        
        const startDateStr = systemFilters.startDate.toISOString().split('T')[0];
        const endDateStr = systemFilters.endDate.toISOString().split('T')[0];
        queryParams.append('startDate', startDateStr);
        queryParams.append('endDate', endDateStr);
      }
      
      const response = await fetch(`/s1/admin/logs?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (!response.ok) {
        setSystemLogsError(data.error || 'Unexpected error occurred!');
        return;
      }
      
      setSystemLogsError('');
      
      if (data.data && Array.isArray(data.data)) {
        setSystemLogs(data.data);
        setFilteredSystemLogs(data.data);
        setChartData(generateChartData(data.data));
      } else {
        setSystemLogs(data);
        setFilteredSystemLogs(data);
        setChartData(generateChartData(data));
      }
    } catch (err) {
      setSystemLogsError(err.message || 'Something went wrong.');
    } finally {
      setSystemLogsLoading(false);
    }
  }, [systemFilters, generateChartData]);

  // Fetch Activity Logs
  const fetchActivityLogs = async () => {
    setActivityLogsLoading(true);
    setActivityLogsError("");
    try {
      const token = getAdminToken();
      const params = {};
      if (activityFilters.activity_type) params.activity_type = activityFilters.activity_type;
      if (activityFilters.username) params.username = activityFilters.username;
      if (activityFilters.status) params.status = activityFilters.status;

      const response = await axios.get(
        "/s1/admin/activity-logs",
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );
      setActivityLogs(response.data);
    } catch (err) {
      setActivityLogsError("Failed to fetch activity logs.");
    } finally {
      setActivityLogsLoading(false);
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    if (activeTab === 0) {
      fetchSystemLogs();
    } else if (activeTab === 1) {
      fetchActivityLogs();
    }
  }, [activeTab, fetchSystemLogs]);

  // System Logs handlers
  const handleSystemFilterChange = (field, value) => {
    setSystemFilters(prev => ({
      ...prev,
      [field]: value,
      customRange: field === 'timeRange' && value === 'custom'
    }));
  };

  const clearSystemFilters = () => {
    setSystemFilters({
      timeRange: 'day',
      startDate: null,
      endDate: null,
      customRange: false
    });
  };

  const handleView = async (logMessage, index) => {
    try {
      setViewing(prev => ({ ...prev, [index]: true }));
      const blob = new Blob([logMessage], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const newTab = window.open(url, '_blank');
      newTab.onload = () => URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setViewing(prev => ({ ...prev, [index]: false }));
    }
  };

  // Activity Logs handlers
  const handleActivityFilterChange = (e) => {
    setActivityFilters({ ...activityFilters, [e.target.name]: e.target.value });
  };

  const applyActivityFilters = () => {
    fetchActivityLogs();
  };

  const clearActivityFilters = () => {
    setActivityFilters({
      activity_type: "",
      username: "",
      status: ""
    });
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Render System Logs Tab
  const renderSystemLogsTab = () => (
    <Box sx={{ mt: 3 }}>
      {/* Custom styles for date picker */}
      <style jsx>{`
        .custom-datepicker-calendar {
          background-color: ${"var(--v2-surface)"} !important;
          border: 1px solid ${"var(--v2-border-strong)"} !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__header {
          background-color: ${"var(--v2-surface)"} !important;
          border-bottom: 1px solid ${"var(--v2-border-strong)"} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__day-name {
          color: ${"var(--v2-text)"} !important;
          font-weight: bold !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__day {
          color: ${"var(--v2-text)"} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__day:hover {
          background-color: ${isDarkMode ? '#444' : '#f0f0f0'} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__day--selected {
          background-color: ${"var(--v2-accent)"} !important;
          color: ${isDarkMode ? '#000' : '#fff'} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__day--in-range {
          background-color: ${isDarkMode ? 'rgba(144, 202, 249, 0.3)' : 'rgba(25, 118, 210, 0.3)'} !important;
          color: ${"var(--v2-text)"} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__day--in-selecting-range {
          background-color: ${isDarkMode ? 'rgba(144, 202, 249, 0.5)' : 'rgba(25, 118, 210, 0.5)'} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__month-dropdown,
        .custom-datepicker-calendar .react-datepicker__year-dropdown {
          background-color: ${"var(--v2-surface)"} !important;
          border: 1px solid ${"var(--v2-border-strong)"} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__month-option,
        .custom-datepicker-calendar .react-datepicker__year-option {
          color: ${"var(--v2-text)"} !important;
        }
        
        .custom-datepicker-calendar .react-datepicker__month-option:hover,
        .custom-datepicker-calendar .react-datepicker__year-option:hover {
          background-color: ${isDarkMode ? '#444' : '#f0f0f0'} !important;
        }
      `}</style>
      
      {/* Filter Section */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: "var(--v2-surface)" }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FaFilter style={{ marginRight: '8px', color: "var(--v2-text)" }} />
          <Typography variant="h6" sx={{ color: "var(--v2-text)" }}>
            Filter System Logs
          </Typography>
        </Box>
        
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              <Typography variant="body2" sx={{ 
                color: "var(--v2-text)",
                fontWeight: 'bold', 
                mb: 1
              }}>
                Time Range
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={systemFilters.timeRange}
                  onChange={(e) => handleSystemFilterChange('timeRange', e.target.value)}
                  sx={{ 
                    color: "var(--v2-text)",
                    backgroundColor: "var(--v2-surface)",
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: "var(--v2-border-strong)",
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? '#666' : '#999',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: "var(--v2-accent)",
                    }
                  }}
                >
                  <MenuItem value="day">Today</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {systemFilters.customRange && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                '& .react-datepicker-wrapper': {
                  width: '100%'
                },
                '& .react-datepicker__input-container': {
                  width: '100%'
                },
                '& .react-datepicker__input-container input': {
                  width: '100%',
                  padding: '16.5px 14px',
                  border: `1px solid ${"var(--v2-border-strong)"}`,
                  borderRadius: '4px',
                  backgroundColor: "var(--v2-surface)",
                  color: "var(--v2-text)",
                  fontSize: '16px',
                  '&:focus': {
                    outline: 'none',
                    borderColor: "var(--v2-accent)",
                    borderWidth: '2px'
                  },
                  '&:hover': {
                    borderColor: isDarkMode ? '#666' : '#999'
                  }
                }
              }}>
                <Typography variant="body2" sx={{ 
                  color: "var(--v2-text)",
                  fontWeight: 'bold',
                  mb: 1 
                }}>
                  Select Date Range
                </Typography>
                <DatePicker
                  ref={datePickerRef}
                  selected={systemFilters.startDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setSystemFilters(prev => ({
                      ...prev,
                      startDate: start,
                      endDate: end
                    }));
                  }}
                  startDate={systemFilters.startDate}
                  endDate={systemFilters.endDate}
                  selectsRange
                  placeholderText="Click to select date range"
                  dateFormat="MMM dd, yyyy"
                  showPopperArrow={true}
                  popperClassName="react-datepicker-popper"
                  className="custom-datepicker-input"
                  calendarClassName="custom-datepicker-calendar"
                  wrapperClassName="custom-datepicker-wrapper"
                  isClearable
                  maxDate={new Date()}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MuiButton
              variant="outlined"
              onClick={clearSystemFilters}
              startIcon={<FaCalendarAlt />}
              sx={{
                color: "var(--v2-text)",
                borderColor: "var(--v2-border-strong)",
                height: '56px',
                '&:hover': {
                  borderColor: isDarkMode ? '#666' : '#999',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Clear Filters
            </MuiButton>
          </Grid>
        </Grid>

        {/* Filter Summary */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`Showing ${filteredSystemLogs.length} days`}
            color="primary"
            variant="outlined"
            sx={{ color: "var(--v2-text)" }}
          />
          <Chip
            label={`Filter: ${systemFilters.timeRange === 'day' ? 'Today' : 
                      systemFilters.timeRange === 'month' ? 'This Month' : 
                      systemFilters.timeRange === 'year' ? 'This Year' : 'Custom Range'}`}
            color="secondary"
            variant="outlined"
            sx={{ color: "var(--v2-text)" }}
          />
          {systemFilters.timeRange === 'custom' && systemFilters.startDate && systemFilters.endDate && (
            <Chip
              label={`Range: ${systemFilters.startDate.toLocaleDateString()} - ${systemFilters.endDate.toLocaleDateString()}`}
              color="warning"
              variant="outlined"
              sx={{ color: "var(--v2-text)" }}
            />
          )}
          {filteredSystemLogs.length > 0 && (
            <Chip
              label={`Total Logs: ${filteredSystemLogs.reduce((sum, day) => sum + (day.count || 0), 0)}`}
              color="success"
              variant="outlined"
              sx={{ color: "var(--v2-text)" }}
            />
          )}
        </Box>
      </Paper>

      {/* Chart Section */}
      {systemFilters.timeRange === 'custom' && (!systemFilters.startDate || !systemFilters.endDate) ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: "var(--v2-text)" }}>
            Select Date Range to View Chart
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--v2-text-secondary)", mt: 1 }}>
            Choose your start and end dates to see the log frequency chart
          </Typography>
        </Box>
      ) : chartData && chartData.labels.length > 0 ? (
        <Line
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: 'top', labels: { color: "var(--v2-text)" } },
              title: { 
                display: true, 
                text: `Log Frequency Over Time (${systemFilters.timeRange === 'day' ? 'Today' : 
                      systemFilters.timeRange === 'month' ? 'This Month' : 
                      systemFilters.timeRange === 'year' ? 'This Year' : 'Custom Range'})`, 
                color: "var(--v2-text)"
              },
            },
            scales: {
              x: { ticks: { color: "var(--v2-text)" } },
              y: { ticks: { color: "var(--v2-text)" } },
            },
          }}
        />
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: "var(--v2-text)" }}>
            {systemFilters.timeRange === 'day' ? 'No logs found for today' : 
             systemFilters.timeRange === 'month' ? 'No logs found for this month' :
             systemFilters.timeRange === 'year' ? 'No logs found for this year' : 
             'No logs found for the selected time range'}
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--v2-text-secondary)", mt: 1 }}>
            {systemFilters.timeRange === 'day' ? 'Logs will appear here once they are generated for today' : 
             'Try selecting a different time range or check back later'}
          </Typography>
        </Box>
      )}

      {/* Daily Logs Matrix */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2, color: "var(--v2-text)" }}>
        Daily Logs Matrix ({filteredSystemLogs.length} days)
      </Typography>

      <MuiTableContainer component={Paper} sx={{ backgroundColor: "var(--v2-surface)" }}>
        <MuiTable>
          <MuiTableHead sx={{ backgroundColor: "var(--v2-surface)" }}>
            <MuiTableRow>
              <MuiTableCell sx={{ color: "var(--v2-text)" }}>Date</MuiTableCell>
              <MuiTableCell sx={{ color: "var(--v2-text)" }}>Day of Week</MuiTableCell>
              <MuiTableCell sx={{ color: "var(--v2-text)" }}>Log Count</MuiTableCell>
              <MuiTableCell sx={{ color: "var(--v2-text)" }}>First Log</MuiTableCell>
              <MuiTableCell sx={{ color: "var(--v2-text)" }}>Last Log</MuiTableCell>
              <MuiTableCell sx={{ color: "var(--v2-text)" }}>Actions</MuiTableCell>
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            {systemFilters.timeRange === 'custom' && (!systemFilters.startDate || !systemFilters.endDate) ? (
              <MuiTableRow>
                <MuiTableCell colSpan={6} sx={{ textAlign: 'center', color: "var(--v2-text)", py: 4 }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: "var(--v2-text)", mb: 1 }}>
                      Select Date Range to View Logs
                    </Typography>
                    <Typography variant="body2" sx={{ color: "var(--v2-text-secondary)" }}>
                      Choose your start and end dates to see the daily logs matrix
                    </Typography>
                  </Box>
                </MuiTableCell>
              </MuiTableRow>
            ) : filteredSystemLogs.length === 0 ? (
              <MuiTableRow>
                <MuiTableCell colSpan={6} sx={{ textAlign: 'center', color: "var(--v2-text)", py: 4 }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: "var(--v2-text)", mb: 1 }}>
                      {systemFilters.timeRange === 'day' ? 'No logs for today' : 
                       systemFilters.timeRange === 'month' ? 'No logs for this month' :
                       systemFilters.timeRange === 'year' ? 'No logs for this year' : 
                       'No logs found for the selected criteria'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "var(--v2-text-secondary)" }}>
                      {systemFilters.timeRange === 'day' ? 'Logs will appear here once they are generated for today' : 
                       'Try selecting a different time range or check back later'}
                    </Typography>
                  </Box>
                </MuiTableCell>
              </MuiTableRow>
            ) : (
              filteredSystemLogs.map((log, index) => (
                <MuiTableRow key={log.date || log.id} hover sx={{ backgroundColor: "var(--v2-surface)" }}>
                  <MuiTableCell sx={{ color: "var(--v2-text)" }}>
                    {log.date || new Date(log.timestamp).toLocaleDateString()}
                  </MuiTableCell>
                  <MuiTableCell sx={{ color: "var(--v2-text)" }}>
                    {log.day_of_week || new Date(log.timestamp).toLocaleDateString('en-US', { weekday: 'long' })}
                  </MuiTableCell>
                  <MuiTableCell sx={{ color: "var(--v2-text)" }}>
                    <Chip 
                      label={log.count || 1} 
                      color={log.count > 100 ? 'error' : log.count > 50 ? 'warning' : 'success'}
                      size="small"
                    />
                  </MuiTableCell>
                  <MuiTableCell sx={{ color: "var(--v2-text)" }}>
                    {log.first_log ? new Date(log.first_log).toLocaleTimeString() : 
                     log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                  </MuiTableCell>
                  <MuiTableCell sx={{ color: "var(--v2-text)" }}>
                    {log.last_log ? new Date(log.last_log).toLocaleTimeString() : 
                     log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                  </MuiTableCell>
                  <MuiTableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleView(log.log_message || `Daily summary for ${log.date}`, index)}
                      title="View daily details"
                    >
                      <FaEye className={viewing[index] ? 'spin_me' : ''} />
                    </IconButton>
                  </MuiTableCell>
                </MuiTableRow>
              ))
            )}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>
    </Box>
  );

  // Render Activity Logs Tab
  const renderActivityLogsTab = () => {
    const themeStyles = {
      backgroundColor: "var(--v2-bg)",
      color: "var(--v2-text)",
      minHeight: "100vh",
      paddingBottom: "50px"
    };

    const cardThemeStyles = {
      backgroundColor: "var(--v2-surface)",
      color: "var(--v2-text)",
      border: 'none',
      borderRadius: '10px',
      boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.1)'
    };

    const inputThemeStyles = {
      backgroundColor: "var(--v2-surface)",
      color: "var(--v2-text)",
      borderColor: "var(--v2-border-strong)",
    };

    return (
      <div className="container py-4" style={themeStyles}>
        <Row className="justify-content-center">
          <Col lg={11}>
            <Card style={cardThemeStyles}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-transparent border-0">
                <h4 className="mb-0">Activity Logs</h4>
                <Button variant="outline-primary" size="sm" onClick={applyActivityFilters} disabled={activityLogsLoading}>
                  <TbFilter className="me-1" /> {activityLogsLoading ? "Loading..." : "Apply Filters"}
                </Button>
              </Card.Header>
              <Card.Body>
                {/* Filters */}
                <Form className="mb-4">
                  <Row className="g-3">
                    <Col md={4}>
                      <FloatingLabel label="Username">
                        <Form.Control
                          type="text"
                          placeholder="Username"
                          name="username"
                          value={activityFilters.username}
                          onChange={handleActivityFilterChange}
                          style={inputThemeStyles}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel label="Activity Type">
                        <Form.Select
                          name="activity_type"
                          value={activityFilters.activity_type}
                          onChange={handleActivityFilterChange}
                          style={inputThemeStyles}
                        >
                          <option value="">All Types</option>
                          <option value="login">Login</option>
                          <option value="password_reset">Password Reset</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col md={4}>
                      <FloatingLabel label="Status">
                        <Form.Select
                          name="status"
                          value={activityFilters.status}
                          onChange={handleActivityFilterChange}
                          style={inputThemeStyles}
                        >
                          <option value="">All Statuses</option>
                          <option value="success">Success</option>
                          <option value="failed">Failed</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                  </Row>
                </Form>

                {/* Alerts */}
                {activityLogsError && <Alert variant="danger">{activityLogsError}</Alert>}

                {/* Logs Table */}
                <div className="table-responsive">
                  <Table
                    hover
                    striped
                    bordered
                    size="sm"
                    className="align-middle"
                    variant={isDarkMode ? "dark" : "light"}
                  >
                    <thead className="table-secondary">
                      <tr>
                        <th>#</th>
                        <th>Username</th>
                        <th>Activity Type</th>
                        <th>Status</th>
                        <th>Message</th>
                        <th>IP</th>
                        <th>User Agent</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-3">No logs found.</td>
                        </tr>
                      ) : (
                        activityLogs.map((log) => (
                          <tr key={log.id}>
                            <td>{log.id}</td>
                            <td>{log.username_attempted}</td>
                            <td>{log.activity_type}</td>
                            <td>
                              <Badge
                                bg={log.status === "success" ? "success" : "danger"}
                                className="text-uppercase"
                              >
                                {log.status}
                              </Badge>
                            </td>
                            <td>{log.message}</td>
                            <td>{log.ip_address}</td>
                            <td style={{ maxWidth: "200px", wordBreak: "break-word" }}>{log.user_agent}</td>
                            <td>{new Date(log.created_at).toLocaleString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: "var(--v2-text)",
              '&.Mui-selected': {
                color: "var(--v2-accent)",
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: "var(--v2-accent)",
            },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaChartLine />
                Reported Logs
                {filteredSystemLogs.length > 0 && (
                  <Badge badgeContent={filteredSystemLogs.length} color="primary" />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaList />
                Activity Logs
                {activityLogs.length > 0 && (
                  <Badge badgeContent={activityLogs.length} color="secondary" />
                )}
              </Box>
            } 
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        systemLogsError ? (
          <Alert severity="error">{systemLogsError}</Alert>
        ) : systemLogsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderSystemLogsTab()
        )
      )}

      {activeTab === 1 && (
        activityLogsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderActivityLogsTab()
        )
      )}
    </Box>
  );
};

export default UnifiedLogs;
